import { IPromise, IComponentOptions } from "angular";
import { IEmployee } from "@gtmhub/employees/models/models";
import { ITeam } from "@gtmhub/teams";
import { TeamService } from "../../teamService";
import { EmployeeService } from "@gtmhub/employees/services/employeeService";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { TeamEvents } from "../../events";
import { GtmhubController, IRestLayerRequest } from "@gtmhub/core";
import { INgRedux } from "@gtmhub/state-management";
import { TeamsActions } from "@gtmhub/teams/redux/teams-actions";
import { ITeamsStoreState } from "@gtmhub/teams/redux/teams-reducer";
import { IdMap } from "@gtmhub/util";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { IModalComponent } from "@gtmhub/core/routing";

interface ITeamMembersStateParamsService {
  teamId: string;
  teamRole: string;
}

const bindStateToCtrl = (state: ITeamsStoreState) => {
  const { map: teamsMap, isFetched: teamsFetched, error: teamsError } = state.teams;
  return {
    teamsMap,
    teamsFetched,
    teamsError,
  };
};

interface ITeamMembersComponentRedux {
  teamsMap: IdMap<ITeam>;
  teamsFetched: boolean;
  teamsError: IUIError;
}

export interface TeamMembersCtrl extends IModalComponent, ITeamMembersComponentRedux {}
export class TeamMembersCtrl extends GtmhubController {
  indicators: {
    loadingTeams?: boolean;
    search?: boolean;
    employees?: boolean;
  } = {};
  team: ITeam;
  employeeSearch: string;
  employees: IEmployee[];
  changeManager: boolean;
  paginator: {
    currentPage: number;
    itemsPerPage: number;
    paginatorLimit: number;
    totalPages?: number;
    employeesTotalCount?: number;
  };

  private teamEvents: TeamEvents;

  static $inject = ["$ngRedux", "TeamsActions", "$scope", "$stateParams", "TeamService", "EmployeeService", "UIErrorHandlingService"];

  constructor(
    $ngRedux: INgRedux,
    teamsActions: TeamsActions,
    private $scope: IExtendedRootScopeService,
    private $stateParams: ITeamMembersStateParamsService,
    private teamService: TeamService,
    private employeeService: EmployeeService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    super();

    this.teamEvents = new TeamEvents(this.$scope);

    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(teamsActions.fetchTeamsIfMissing());
  }

  $onInit(): void {
    this.indicators.loadingTeams = true;

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.teamsFetched,
        () => this.teamsError
      )
      .then(
        () => {
          this.team = this.teamsMap[this.$stateParams.teamId];
          this.indicators.loadingTeams = false;

          this.setDefaultPaginator();
          this.getEmployees();
        },
        () => {
          this.indicators.loadingTeams = false;
          this.uiErrorHandlingService.handleModal(this.teamsError);
        }
      );

    this.changeManager = this.$stateParams.teamRole === "manager" ? true : false;
  }

  searchEmployees(query: string): void {
    this.employeeSearch = query;
    this.setDefaultPaginator();
    this.getEmployees("search", this.employeeSearch);
  }

  clearSearch(): void {
    this.setDefaultPaginator();
    this.employeeSearch = "";

    this.getEmployees("search");
  }

  onPageChange(page: number): void {
    this.paginator.currentPage = page;
    this.getEmployees("search", this.employeeSearch);
  }

  setManager(employeeId: string): void {
    this.teamService.setTeamManager(this.team.id, employeeId).then(
      () => {
        this.team.manager = employeeId;
        this.team.managerInfo = this.employees.find((e) => e.id === employeeId);
        this.teamEvents.broadcastTeamUpdated({ team: this.team });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  removeManager(): void {
    this.teamService.removeTeamManager(this.team.id).then(
      () => {
        this.team.manager = "";
        this.team.managerInfo = {};

        this.teamEvents.broadcastTeamUpdated({ team: this.team });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  addMember(employeeId: string): void {
    this.teamService.addTeamMember(this.team.id, [employeeId]).then(
      () => {
        for (let i = 0; i < this.employees.length; i++) {
          if (this.employees[i].id === employeeId) {
            if (!this.team.members) {
              this.team.members = [];
            }
            if (!this.team.membersInfo) {
              this.team.membersInfo = [];
            }
            this.team.members.push(employeeId);
            this.team.membersInfo.push(this.employees[i]);
          }
        }

        this.teamEvents.broadcastTeamUpdated({ team: this.team });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  removeMember(employeeId: string): void {
    this.teamService.removeTeamMember(this.team.id, employeeId).then(
      () => {
        const index = this.team.members.indexOf(employeeId);
        this.team.members.splice(index, 1);
        this.team.membersInfo.splice(index, 1);

        this.teamEvents.broadcastTeamUpdated({ team: this.team });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private setDefaultPaginator(): void {
    this.paginator = {
      currentPage: 1,
      itemsPerPage: 15,
      paginatorLimit: 5,
    };
  }

  private getEmployees = (indicator = "employees", employeeName?: string): IPromise<unknown> => {
    this.indicators[indicator] = true;

    const queryParams: IRestLayerRequest = {
      skip: (this.paginator.currentPage - 1) * this.paginator.itemsPerPage,
      limit: this.paginator.itemsPerPage,
      fields: "id,picture,email,firstName,lastName,teamIds,isActive",
    };

    if (employeeName) {
      queryParams.filter = {
        $or: [{ firstName: { $regex: employeeName } }, { lastName: { $regex: employeeName } }],
      };
    }

    return this.employeeService.getEmployeesV2(queryParams).then(
      (employeesCollection) => {
        this.indicators[indicator] = false;

        this.employees = employeesCollection.items;
        this.paginator.totalPages = Math.ceil(employeesCollection.totalCount / this.paginator.itemsPerPage);
        this.paginator.employeesTotalCount = employeesCollection.totalCount;
      },
      (error) => {
        this.indicators[indicator] = false;
        this.uiErrorHandlingService.handleIndicator(error, { indicator, inline: true });
      }
    );
  };
}

export const TeamMembersComponent: IComponentOptions = {
  controller: TeamMembersCtrl,
  bindings: {
    dismiss: "&",
    close: "&",
  },
  templateUrl: require("./team-members.component.jade"),
};
